import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  IconButton,
  Link,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { PROGRESSION_MODEL_SLIDER_HEIGHT } from 'features/wildfires/wildfire.constants';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';
import { usePoisState } from 'state/usePoisState';
import { useOverlappingFeaturesState } from 'state/useOverlappingFeaturesState';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { makeStyles } from 'tss-react/mui';
import { useMapEntityDrawerState } from 'features/mapping/hooks/useMapEntityDrawerState';
import { getMapDialogCommonStyles } from 'features/mapping/mapping.utils';
import { useMapEntityDrawer } from 'features/mapping/hooks/useMapEntityDrawer';
import { useMapMode } from 'hooks/useMapMode';
import { TIMELINE_MODAL_HEIGHT } from 'features/mapping/components/timeline/timeline.constants';
import { LIGHTNING_TOOLS_CARD_HEIGHT } from 'features/mapping/components/layers/LightningLayer/LightningToolsCard';
import { useIsLightningToolsVisible } from 'features/mapping/hooks/useIsLightningToolsVisible';
import { ElectricRetailDialogContent } from './ElectricRetailDialogContent';
import { LightningDialogContent } from './LightningDialogContent';
import { PowerOutageDialogContent } from './PowerOutageDialogContent';
import { RadioTowerDialogContent } from './RadioTowerDialogContent';
import { ResponsibilityDialogContent } from './ResponsibilityDialogContent';
import { ElectricLineDialogContent } from './ElectricLineDialogContent';
import {
  MAP_DOM_ELEMENT_ID,
  WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH,
} from '../../../../../constants';
import { ActiveFirePerimeterDialogContent } from './ActiveFirePerimeterDialogContent';
import { AircraftDialogContent } from './AircraftDialogContent';
import { EvacZoneDialogContent } from './EvacZoneDialogContent';
import { MeasureDistanceToolDialogContent } from './MeasureDistanceToolDialogContent';
import { PlaceDialogContent } from './PlaceDialogContent';
import { FemaFloodHazardDialog } from './FemaFloodHazardDialog';
import { NgfsDetectionDialogContent } from './NgfsDetectionDialogContent';
import { Poi, PoiMetadataByType } from './PoiDialog.types';

// externalGeoEvents render in cards, not the POI Dialog
type RenderablePoiType = Exclude<keyof PoiMetadataByType, 'externalGeoEvent'>;

type DialogComponents = {
  [K in RenderablePoiType]: PoiMetadataByType[K] extends null
    ? () => JSX.Element
    : (props: PoiMetadataByType[K]) => JSX.Element;
};

type DialogLinkHooks = {
  [K in keyof PoiMetadataByType]?: (
    props: PoiMetadataByType[K],
  ) => string | null;
};

const POI_DIALOG_COMPONENTS: DialogComponents = {
  firePerimeter: ActiveFirePerimeterDialogContent,
  // @todo remove 'PlaceDialogContent'' when removing 'pin_drop_improvements' release flag
  legacyPlace: PlaceDialogContent,
  powerOutage: PowerOutageDialogContent,
  lightning: LightningDialogContent,
  radioTower: RadioTowerDialogContent,
  aircraft: AircraftDialogContent,
  electricRetail: ElectricRetailDialogContent,
  responsibilityArea: ResponsibilityDialogContent,
  evacZone: EvacZoneDialogContent,
  activeEvacZone: EvacZoneDialogContent,
  electricalLine: ElectricLineDialogContent,
  gasPipeline: ElectricLineDialogContent,
  measureTool: MeasureDistanceToolDialogContent,
  femaFloodHazard: FemaFloodHazardDialog,
  ngfsDetection: NgfsDetectionDialogContent,
};

const POI_DIALOG_LINK_HOOKS: DialogLinkHooks = {};

const PoiDialogContent = (poi: Poi): JSX.Element | null => {
  const { type, metadata, id } = poi;
  if (type === 'externalGeoEvent') return null;
  const PoiDialogComponent = POI_DIALOG_COMPONENTS[type];

  // The "key" prop is used to force a re-render of the component when the type changes
  if (type === 'measureTool') {
    // @ts-expect-error - we know metadata is undefined and component takes no props
    return <PoiDialogComponent key={type} />;
  }

  const props = metadata ? { ...metadata } : { sourceId: type, featureId: id };

  return (
    // @ts-expect-error - we know metadata is of the correct type
    <PoiDialogComponent key={type} {...props} />
  );
};

const PoiMoreLink = (poi: Poi): JSX.Element | null => {
  const { type, metadata, id } = poi;
  const { t } = useTranslation();
  // hook is guaranteed to exist if we are in this component, see MoreLink wrapper below
  const usePoiDialogLink = POI_DIALOG_LINK_HOOKS[type];
  // @ts-expect-error - we know metadata is of the correct type
  const moreLink = usePoiDialogLink({
    ...(metadata ?? {}),
    sourceId: type,
    featureId: id,
  });

  if (!moreLink) return null;

  return (
    <Button
      component={Link}
      href={moreLink}
      target="_blank"
      underline="none"
      color="neutral"
      size="small"
      sx={{ pointerEvents: 'auto' }}
    >
      {t('common.more')}
    </Button>
  );
};

// Wrapper to avoid conditionally calling hooks
const MoreLink = (poi?: Poi): JSX.Element | null => {
  if (!poi) return null;
  const { type } = poi;
  if (!POI_DIALOG_LINK_HOOKS[type]) return null;
  return <PoiMoreLink {...poi} />;
};

/** Gap below progression time slider to avoid overlapping with the POI dialog. */
const PROGRESSION_MODEL_POI_GAP_Y = 16;
/**
 * On desktop, overlapping-features UI (<LayersNavControl>) sits above the entity drawer,
 * so the POI dialog needs extra lift to avoid overlapping. On mobile the dialog is already
 * above layers control, so no extra lift is needed.
 */
const DESKTOP_OVERLAPPING_FEATURES_EXTRA_Y = 32;

type PoiDialogExtraYOffsetParams = {
  hasOverlappingFeatures: boolean;
  isLightningToolsVisible: boolean;
  isMobile: boolean;
  isProgressionModelSelected: boolean;
  isTimelineMode: boolean;
};

const getExtraYOffset = ({
  hasOverlappingFeatures,
  isLightningToolsVisible,
  isMobile,
  isProgressionModelSelected,
  isTimelineMode,
}: PoiDialogExtraYOffsetParams): number => {
  const desktopOverlappingExtraY =
    !isMobile && hasOverlappingFeatures
      ? DESKTOP_OVERLAPPING_FEATURES_EXTRA_Y
      : 0;

  if (isProgressionModelSelected) {
    return (
      PROGRESSION_MODEL_SLIDER_HEIGHT +
      PROGRESSION_MODEL_POI_GAP_Y +
      desktopOverlappingExtraY
    );
  }

  if (isTimelineMode) {
    return isMobile
      ? TIMELINE_MODAL_HEIGHT.mobile
      : TIMELINE_MODAL_HEIGHT.desktop;
  }

  if (isLightningToolsVisible) {
    return (
      (isMobile
        ? LIGHTNING_TOOLS_CARD_HEIGHT.mobile
        : LIGHTNING_TOOLS_CARD_HEIGHT.desktop) + desktopOverlappingExtraY
    );
  }

  return desktopOverlappingExtraY;
};

const useStyles = makeStyles<{
  isDrawerEntitySelected: boolean;
  isSearchDrawerOpen: boolean;
  minSnapPct: number;
  isProgressionModelSelected: boolean;
  isTimelineMode: boolean;
  isMobile: boolean;
  hasOverlappingFeatures: boolean;
  isLightningToolsVisible: boolean;
}>()((
  theme,
  {
    isDrawerEntitySelected,
    isSearchDrawerOpen,
    minSnapPct,
    isProgressionModelSelected,
    isTimelineMode,
    isMobile,
    hasOverlappingFeatures,
    isLightningToolsVisible,
  },
) => {
  const extraYOffset = getExtraYOffset({
    hasOverlappingFeatures,
    isLightningToolsVisible,
    isMobile,
    isProgressionModelSelected,
    isTimelineMode,
  });

  return {
    ...getMapDialogCommonStyles(theme, {
      isDrawerEntitySelected,
      isSearchDrawerOpen,
      minWidth: 362,
      minSnapPct,
      extraYOffset,
      trackStatusAlertOffset: true,
    }),
    closeButton: {
      position: 'absolute',
      top: 8,
      right: 8,
      pointerEvents: 'auto',
    },
  };
});

export const PoiDialog = (): JSX.Element | null => {
  const { open: isSearchDrawerOpen } = useSearchDrawerState();
  const { open: isDrawerEntitySelected, minSnapPct } =
    useMapEntityDrawerState();
  const { isTimelineMode } = useMapMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

  const location = useLocation();
  const isProgressionModelSelected = Boolean(
    matchPath(location.pathname, {
      path: WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH,
      exact: true,
    }),
  );

  const { clearOverlappingFeatures, hasOverlappingFeatures } =
    useOverlappingFeaturesState();

  const isLightningToolsVisible = useIsLightningToolsVisible();

  const { classes } = useStyles({
    isDrawerEntitySelected,
    isSearchDrawerOpen,
    minSnapPct,
    isProgressionModelSelected,
    isTimelineMode,
    isMobile,
    hasOverlappingFeatures,
    isLightningToolsVisible,
  });

  const {
    selectedPois,
    selectedPoi,
    activeIndex,
    clearSelectedPois,
    selectPreviousPoi,
    selectNextPoi,
  } = usePoisState();
  const { t } = useTranslation();
  const { restoreEntityDrawer } = useMapEntityDrawer();

  const handleClose = (): void => {
    clearOverlappingFeatures();
    clearSelectedPois();
    restoreEntityDrawer();
  };

  const LinkComponent = MoreLink(selectedPoi);
  const showNavigation = !hasOverlappingFeatures && selectedPois.length > 1;

  return (
    <Dialog
      open={Boolean(selectedPois.length)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      classes={{
        root: classes.root,
        container: classes.container,
        paper: classes.paper,
      }}
      hideBackdrop
      container={(): HTMLElement | null =>
        document.getElementById(MAP_DOM_ELEMENT_ID)
      }
      onClose={handleClose}
    >
      {selectedPoi && <PoiDialogContent {...selectedPoi} />}

      {(showNavigation || LinkComponent) && (
        <Stack direction="row" spacing={1} sx={{ pb: 2, px: 2 }}>
          {LinkComponent}

          {showNavigation && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: 'flex-end', flex: 1 }}
            >
              <Button
                startIcon={<ChevronLeftIcon />}
                onClick={selectPreviousPoi}
                disabled={activeIndex === 0}
                color="neutral"
                size="small"
                sx={{
                  pointerEvents: 'auto',
                  '& .MuiButton-startIcon': { mr: 0 },
                }}
              >
                {t('common.prev')}
              </Button>

              <Button
                endIcon={<ChevronRightIcon />}
                onClick={selectNextPoi}
                disabled={activeIndex === selectedPois.length - 1}
                color="neutral"
                size="small"
                sx={{
                  pointerEvents: 'auto',
                  '& .MuiButton-endIcon': { ml: 0 },
                }}
              >
                {t('common.next')}
              </Button>
            </Stack>
          )}
        </Stack>
      )}

      <IconButton
        size="small"
        aria-label={t('common.close')}
        className={classes.closeButton}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
    </Dialog>
  );
};
